import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'src/components/queryProvider';
import { COLLECTIONS } from 'src/constants/constants';
import { BASE_CONFIG, DYR_CONFIG } from 'src/constants/gcfSectionConfig';
import {
  ICanvasSection,
  ICreateSectionPayload,
  ILayoutConfig,
  IScreen,
} from 'src/interfaces';
import {
  generateQuery,
  isCurrentScreenWithDYR,
  parseLayoutConfig,
  queryKeys,
  requestCCApi,
} from 'src/utils';
import { checkSectionTypes } from 'src/utils/section-types-helper';
import { uid } from 'uid';

export const getOrCreateSectionId = async (
  section: { height: number; width: number; rows?: number; cols?: number },
  sectionTypeID?: number,
): Promise<number> => {
  try {
    const filters = [`width=${section.width}`, `height=${section.height}`];
    const query = generateQuery([], filters);

    const response = await requestCCApi({
      url: `/sections${query}`,
      method: 'GET',
    });

    if (response?.data?.length > 0) return response.data[0]?.id;

    const newSection = await requestCCApi({
      url: `/sections`,
      method: 'POST',
      data: {
        data: {
          name: `${section.cols}x${section.rows} Block`,
          section_type_id: sectionTypeID,
          width: section.width,
          height: section.height,
        },
      },
    });

    return newSection?.id;
  } catch (error) {
    console.error('Failed to fetch or create section:', error);
    throw error;
  }
};

export const getOrCreateSectionTypeId = async (
  layoutGridLine: string,
  screen: IScreen,
) => {
  const { orientation, rows, cols, disclaimerPosition }: ILayoutConfig =
    parseLayoutConfig(layoutGridLine);
  const isVertical = orientation === 'vertical';
  const disclaimerHeight = isVertical ? screen.height / 12 : screen.height / 15;
  const adjustedHeight = disclaimerPosition
    ? screen.height - disclaimerHeight
    : screen.height;
  const gridWidth = screen.width / cols;
  const gridHeight = adjustedHeight / rows;

  const filters = [
    `grid_width=${Math.round(gridWidth)}`,
    `grid_height=${Math.round(gridHeight)}`,
  ];
  const query = generateQuery([], filters);

  const response = await requestCCApi({
    url: `/sectionTypes${query}`,
    method: 'GET',
  });

  if (response?.data?.length > 0) return response.data?.[0]?.id;

  const newSectionType = await requestCCApi({
    url: `/sectionTypes`,
    method: 'POST',
    data: {
      data: {
        name: `${orientation} ${cols}x${rows} Grid${
          disclaimerPosition ? ' + Disclaimer' : ''
        }`,
        grid_width: Math.round(gridWidth),
        grid_height: Math.round(gridHeight),
      },
    },
  });

  return newSectionType?.id;
};

export const createScreenSection = async (payload: ICreateSectionPayload) => {
  const sectionTypeID = await getOrCreateSectionTypeId(
    payload.layoutGridLine,
    payload.screen,
  );

  const { hasDYR, hasTakeOver, hasSectionRotation } = checkSectionTypes(
    payload.sectionTypes,
  );
  const sectionCache = new Map<string, Promise<number>>();

  const getOrCacheSectionId = async (section: ICanvasSection) => {
    const sectionKey = `${section.width}x${section.height}x${section.rows}x${section.cols}`;
    if (!sectionCache.has(sectionKey)) {
      const sectionPromise = getOrCreateSectionId(section, sectionTypeID);
      sectionCache.set(sectionKey, sectionPromise);
    }

    return sectionCache.get(sectionKey);
  };

  let screenID = payload.screen?.id;
  const transactions = [];

  if (payload.screen.name.includes('placeholder')) {
    const uniqueScreenName = `Screen-${uid()}`;
    const screenPayload = {
      data: {
        name: uniqueScreenName,
        section_type_id: sectionTypeID,
        width: payload.screen.width,
        height: payload.screen.height,
        config: {},
      },
    };

    const screenResponse = await requestCCApi({
      url: '/screens',
      method: 'POST',
      data: screenPayload,
    });
    screenID = screenResponse?.id;

    transactions.push({
      resource: COLLECTIONS.LAYOUT_SCREENS,
      id: payload.screen.layoutScreenId,
      method: 'DELETE',
    });

    transactions.push({
      resource: COLLECTIONS.LAYOUT_SCREENS,
      method: 'POST',
      data: {
        layout_id: Number(payload.layoutId),
        channel_screen_sequence: payload.screen.channel_screen_sequence,
        screen_id: Number(screenID),
      },
    });
  }

  const existingSections = !payload.screen.name.includes('placeholder')
    ? payload.screen.sections.filter(
        (section) => section.config?.events === undefined,
      )
    : [];
  const newSections = payload.sections.filter(
    (section) => section.config?.events === undefined,
  );
  const dynamicSections =
    payload.screen.sections.filter(
      (section) => section?.config?.events !== undefined,
    ).length > 0
      ? []
      : payload.sections.filter(
          (section) => section?.config?.events !== undefined,
        );

  const sectionsToDelete = existingSections.filter(
    (existingSection) =>
      !newSections.some(
        (newSection) =>
          newSection.leftOffset === existingSection.left_offset &&
          newSection.topOffset === existingSection.top_offset &&
          newSection.width === existingSection.width &&
          newSection.height === existingSection.height,
      ),
  );

  const sectionsToCreate = newSections.filter(
    (newSection) =>
      !existingSections.some(
        (existingSection) =>
          newSection.leftOffset === existingSection.left_offset &&
          newSection.topOffset === existingSection.top_offset &&
          newSection.width === existingSection.width &&
          newSection.height === existingSection.height,
      ),
  );

  if (!payload.screen.name.includes('placeholder') && sectionsToDelete.length) {
    sectionsToDelete.forEach((section) => {
      transactions.push({
        resource: COLLECTIONS.SCREEN_SECTIONS,
        id: section.screenSectionId,
        method: 'DELETE',
      });
    });
  }

  const dynamicSectionTransactions = await Promise.all(
    dynamicSections.map(async (section) => ({
      resource: COLLECTIONS.SCREEN_SECTIONS,
      method: 'POST',
      data: {
        section_id: await getOrCacheSectionId(section),
        left_offset: section.leftOffset,
        top_offset: section.topOffset,
        config: section.config,
        screen_id: screenID,
      },
    })),
  );

  transactions.push(...dynamicSectionTransactions);

  const newSectionTransactions = await Promise.all(
    sectionsToCreate.map(async (section) => {
      const isRecommendationSection =
        hasDYR &&
        section.height === 440 &&
        section.width === 1080 &&
        section.leftOffset === 0 &&
        (section.topOffset === 1480 || section.topOffset === 1320) &&
        isCurrentScreenWithDYR(
          payload.screen.channel_screen_sequence,
          payload.globalComponentsScreen,
        );

      const configPayload =
        hasSectionRotation || hasTakeOver ? BASE_CONFIG : {};

      return {
        resource: COLLECTIONS.SCREEN_SECTIONS,
        method: 'POST',
        data: {
          section_id: await getOrCacheSectionId(section),
          left_offset: section.leftOffset,
          top_offset: section.topOffset,
          config: isRecommendationSection ? DYR_CONFIG : configPayload,
          screen_id: screenID,
        },
      };
    }),
  );

  transactions.push(...newSectionTransactions);

  if (transactions.length) {
    await requestCCApi({
      url: '/transaction',
      method: 'POST',
      data: { data: transactions },
    });
  }
};

export const useCreateScreenSection = () => {
  return useMutation({
    mutationFn: createScreenSection,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.layoutScreens.invalidateAll());
      queryClient.invalidateQueries(queryKeys.screenSections.invalidateAll());
      queryClient.invalidateQueries(queryKeys.layouts.invalidateAll());
    },
  });
};
